import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import StudentDetailModal from "../../components/StudentDetailModal";
import s from "./MyStudentsPage.module.css";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

const AVATAR_COLORS = ["#5a4e3a", "#3a4e4a", "#4a3a5a", "#4e4a3a", "#3a4a5a", "#5a3a3a", "#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last  || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

/** Workspace data for the Students page — same teacher-scoped endpoints
 *  as the Gradebook hub. Kept separate so each page owns its own load
 *  contract (the gradebook needs offerings + per-offering grades, the
 *  students page doesn't). */
function useTeacherStudentsData(teacherStaffId) {
  const [state, setState] = useState({
    loading: true,
    error: "",
    students: [],
    sections: [],
    gradeLevels: [],
    schoolYears: [],
    semesters: [],
    enrollments: [],
    rooms: [],
  });

  useEffect(() => {
    if (!teacherStaffId) {
      setState(p => ({ ...p, loading: false }));
      return undefined;
    }
    let cancelled = false;
    setState(p => ({ ...p, loading: true, error: "" }));
    const safe = (promise, fallback) => promise.then(r => r.ok ? r.json() : fallback).catch(() => fallback);
    Promise.all([
      safe(fetch(`${API_BASE}/api/students?teacherStaffId=${teacherStaffId}`), []),
      safe(fetch(`${API_BASE}/api/sections?teacherStaffId=${teacherStaffId}`), []),
      safe(fetch(`${API_BASE}/api/grade-levels`), []),
      safe(fetch(`${API_BASE}/api/school-years`), []),
      safe(fetch(`${API_BASE}/api/semesters`), []),
      safe(fetch(`${API_BASE}/api/enrollments?teacherStaffId=${teacherStaffId}`), []),
      safe(fetch(`${API_BASE}/api/rooms`), []),
    ]).then(([students, sections, gradeLevels, schoolYears, semesters, enrollments, rooms]) => {
      if (cancelled) return;
      setState({
        loading: false,
        error: "",
        students:    Array.isArray(students)    ? students    : [],
        sections:    Array.isArray(sections)    ? sections    : [],
        gradeLevels: Array.isArray(gradeLevels) ? gradeLevels : [],
        schoolYears: Array.isArray(schoolYears) ? schoolYears : [],
        semesters:   Array.isArray(semesters)   ? semesters   : [],
        enrollments: Array.isArray(enrollments) ? enrollments : [],
        rooms:       Array.isArray(rooms)       ? rooms       : [],
      });
    }).catch(e => {
      if (!cancelled) setState(p => ({ ...p, loading: false, error: e.message || "Failed to load" }));
    });
    return () => { cancelled = true; };
  }, [teacherStaffId]);

  return state;
}

export default function MyStudentsPage() {
  const user = getUser();
  const teacherStaffId = user?.linkedEntity?.id;
  const data = useTeacherStudentsData(teacherStaffId);

  const [sectionFilter, setSectionFilter] = useState(""); // "" = all
  const [search,        setSearch]        = useState("");
  const [activeStudent, setActiveStudent] = useState(null);

  const sectionsById = useMemo(
    () => Object.fromEntries(data.sections.map(r => [r.id, r])),
    [data.sections]
  );
  const gradeLevelsById = useMemo(
    () => Object.fromEntries(data.gradeLevels.map(r => [r.id, r])),
    [data.gradeLevels]
  );

  // Section label for a student — derived from their currentSectionId via
  // the sections lookup. Falls back to "—" if unresolvable.
  function sectionLabelFor(st) {
    const sec = st?.currentSectionId ? sectionsById[st.currentSectionId] : null;
    if (!sec) return null;
    const grade = sec.gradeLevelId ? gradeLevelsById[sec.gradeLevelId] : null;
    return grade ? `${grade.name} · ${sec.name}` : sec.name;
  }

  // Build the dropdown options from the sections the teacher actually
  // teaches/advises — same idiom as the Gradebook hub. Sorts by grade
  // then by section name so it matches the listing below.
  const sectionOptions = useMemo(() => {
    const opts = data.sections.map(sec => {
      const grade = sec.gradeLevelId ? gradeLevelsById[sec.gradeLevelId] : null;
      return {
        id: sec.id,
        label: grade ? `${grade.name} · ${sec.name}` : sec.name,
        ordering: grade?.ordering ?? 999,
      };
    });
    opts.sort((a, b) => {
      if (a.ordering !== b.ordering) return a.ordering - b.ordering;
      return a.label.localeCompare(b.label);
    });
    return opts;
  }, [data.sections, gradeLevelsById]);

  // Filtered + sorted student list — alphabetical by last name then first
  // name, matching the directory module's convention.
  const visibleStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.students
      .filter(st => !sectionFilter || String(st.currentSectionId) === String(sectionFilter))
      .filter(st => {
        if (!q) return true;
        const full = `${st.lastName || ""} ${st.firstName || ""}`.toLowerCase();
        return full.includes(q)
            || (st.studentNumber || "").toLowerCase().includes(q);
      })
      .slice()
      .sort((a, b) => {
        const ln = (a.lastName || "").localeCompare(b.lastName || "");
        if (ln !== 0) return ln;
        return (a.firstName || "").localeCompare(b.firstName || "");
      });
  }, [data.students, sectionFilter, search]);

  const stats = useMemo(() => {
    const total = data.students.length;
    const sectionSet = new Set();
    let active = 0;
    for (const st of data.students) {
      if (st.currentSectionId) sectionSet.add(st.currentSectionId);
      const status = String(st.status || "ENROLLED").toUpperCase();
      if (status === "ENROLLED") active++;
    }
    return { total, sections: sectionSet.size, active };
  }, [data.students]);

  if (!teacherStaffId) {
    return (
      <div className={s.page}>
        <div className={s.empty}>
          Your account isn't linked to a Staff record yet. Ask an admin to
          set <code>linkedEntityType=STAFF</code> and <code>linkedEntityId</code>
          on your user.
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      {/* ── Premium hero ──────────────────────────────────────── */}
      <header className={s.hero}>
        <div className={s.heroIcon} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div className={s.heroBody}>
          <div className={s.eyebrow}>Teacher · Students</div>
          <h1 className={s.heroTitle}>Your students.</h1>
          <p className={s.heroSub}>
            Every student you teach or advise this term. Click any card to
            open their full profile — contact, current section, and
            enrolment history.
          </p>
        </div>
      </header>

      {/* ── Stat tiles ────────────────────────────────────────── */}
      <div className={s.statRow}>
        <StatTile
          label="Students"
          value={data.loading ? "…" : stats.total}
          hint={data.loading ? "—" : `unique students across your sections`}
        />
        <StatTile
          label="Sections"
          value={data.loading ? "…" : stats.sections}
          hint={data.loading ? "—" : `current cohorts`}
          tone="info"
        />
        <StatTile
          label="Enrolled"
          value={data.loading ? "…" : stats.active}
          hint={data.loading ? "—" : (stats.active === stats.total
            ? "all students active"
            : `${stats.total - stats.active} not currently enrolled`)}
          tone={stats.active === stats.total ? "good" : "warn"}
        />
      </div>

      {/* ── Filter bar ────────────────────────────────────────── */}
      <div className={s.filterBar}>
        <label className={s.filterField}>
          <span>Section</span>
          <select
            value={sectionFilter}
            onChange={e => setSectionFilter(e.target.value)}>
            <option value="">All sections</option>
            {sectionOptions.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>

        <label className={`${s.filterField} ${s.searchField}`}>
          <span>Search</span>
          <div className={s.searchWrap}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                 className={s.searchIcon} aria-hidden="true">
              <circle cx="9" cy="9" r="6"/>
              <path d="M15 15l-3.5-3.5"/>
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Name or student number…"
            />
            {search && (
              <button
                type="button"
                className={s.clearBtn}
                onClick={() => setSearch("")}
                aria-label="Clear search">✕</button>
            )}
          </div>
        </label>

        <div className={s.summaryPills}>
          <span className={s.summaryPill}>
            {data.loading ? "…" : `${visibleStudents.length} shown`}
          </span>
        </div>
      </div>

      {/* ── Loading skeleton ──────────────────────────────────── */}
      {data.loading && (
        <div className={s.skel}>
          <div className={s.skelCard} />
          <div className={s.skelCard} />
          <div className={s.skelCard} />
          <div className={s.skelCard} />
          <div className={s.skelCard} />
          <div className={s.skelCard} />
        </div>
      )}

      {!data.loading && data.error && (
        <div className={s.error}>{data.error}</div>
      )}

      {/* ── Empty state ───────────────────────────────────────── */}
      {!data.loading && !data.error && visibleStudents.length === 0 && (
        <div className={s.emptyPremium}>
          <div className={s.emptyPremiumTitle}>
            {data.students.length === 0
              ? "No students linked to your account yet."
              : "No students match the current filters."}
          </div>
          <div className={s.emptyPremiumSub}>
            {data.students.length === 0
              ? "Once you're assigned to class offerings or named as an adviser, your students will appear here."
              : <>Try a different section, or clear the search.</>}
          </div>
        </div>
      )}

      {/* ── Student grid ──────────────────────────────────────── */}
      {!data.loading && !data.error && visibleStudents.length > 0 && (
        <div className={s.grid}>
          {visibleStudents.map((st, idx) => {
            const fullName = `${st.lastName || ""}, ${st.firstName || ""}`.replace(/^, |, $/g, "");
            const status   = String(st.status || "ENROLLED").toUpperCase();
            const secLabel = sectionLabelFor(st);
            return (
              <button
                key={st.id}
                type="button"
                className={s.card}
                onClick={() => setActiveStudent(st)}
                style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
                aria-label={`Open profile for ${fullName}`}>
                {st.photoUrl
                  ? <img src={st.photoUrl} alt="" className={s.avatarImg} />
                  : (
                    <div className={s.avatar}
                         style={{ background: avatarColor(fullName) }}>
                      {initials(st.firstName, st.lastName)}
                    </div>
                  )}
                <div className={s.cardBody}>
                  <div className={s.cardName} title={fullName}>{fullName || "Unnamed"}</div>
                  <div className={s.cardMeta}>
                    {st.studentNumber && (
                      <span className={s.metaMono}>#{st.studentNumber}</span>
                    )}
                    {secLabel && <span className={s.metaPill}>{secLabel}</span>}
                  </div>
                  <span className={`${s.statusPill} ${s["statusPill_" + status.toLowerCase()] || s.statusPill_other}`}>
                    {status}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Student detail modal ──────────────────────────────── */}
      <StudentDetailModal
        open={!!activeStudent}
        student={activeStudent}
        sections={data.sections}
        gradeLevels={data.gradeLevels}
        schoolYears={data.schoolYears}
        semesters={data.semesters}
        rooms={data.rooms}
        enrollments={data.enrollments}
        onClose={() => setActiveStudent(null)}
      />
    </div>
  );
}

function StatTile({ label, value, hint, tone }) {
  return (
    <div className={`${s.statTile} ${tone ? s["statTile_" + tone] : ""}`}>
      <div className={s.statLabel}>{label}</div>
      <div className={s.statValue}>{value}</div>
      <div className={s.statHint} title={hint}>{hint}</div>
    </div>
  );
}
